<template>
  <div
    :class="['gam-chip', getClasses, getContainerClasses]"
    :role="isInteractive ? 'button' : undefined"
    :tabindex="isInteractive ? 0 : undefined"
    :aria-pressed="getAriaPressed"
    @click="props.onClick"
    @keydown="handleKeydown"
  >
    <div v-if="getLeftIcon" class="icon">
      <gam-icon v-bind="getLeftIcon" />
    </div>
    <div v-if="typeof props.dotsRating === 'number'" class="content">
      <GamRatingDots :rating="props.dotsRating" />
    </div>
    <div v-else-if="typeof label === 'string'" class="content" v-html="getLabel()"></div>
    <div v-else class="content">
      <slot />
    </div>
    <div v-if="getRightIcon" class="icon">
      <gam-icon v-bind="getRightIcon" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import GamRatingDots from '@/views/components/GamRatingDots.vue';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import { computed, useAttrs, useSlots } from 'vue';

const props = withDefaults(defineProps<GamChipType>(), {
  size: GamChipSize.NORMAL,
  variant: GamChipVariant.DEFAULT,
});

const slots = useSlots();
const attrs = useAttrs();

/* Selection groups bind a plain @click rather than passing onClick, so both count
   as interactive. */
const isInteractive = computed(() => Boolean(props.onClick || attrs.onClick));

/* A chip the player can click whose selected state the caller tracks is a toggle,
   and only toggles carry the dimmed/lit selection styling. */
const isToggle = computed(() => isInteractive.value && typeof props.isActive === 'boolean');

/* The glyph rides the chip's own type scale: anything wider than the label it
   sits next to reads as the subject of the chip rather than a qualifier. */
const ICON_SIZE_BY_CHIP_SIZE: Record<GamChipSize, string> = {
  [GamChipSize.NORMAL]: '13px',
  [GamChipSize.SMALL]: '12px',
  [GamChipSize.X_SMALL]: '10px',
};

const getIconValues = (name: GamIconName): GamIconType => {
  return {
    name,
    size: iconSize.value,
    isCurrent: true,
  };
};

const getLabel = (): string => {
  if (props.label) return localize(props.label);
  return '';
};

const getClasses = computed(() => ({
  skeleton: !props.label && !slots.default && typeof props.dotsRating !== 'number',
  'x-small': props.size === GamChipSize.X_SMALL,
  small: props.size === GamChipSize.SMALL,
  normal: props.size === GamChipSize.NORMAL,
  'upper-case': props.isUpperCase,
  'has-dots': typeof props.dotsRating === 'number',
  'is-clickable': isInteractive.value,
  'is-highlight': props.isHighlight,
  'is-toggle': isToggle.value,
}));

const iconSize = computed((): string => ICON_SIZE_BY_CHIP_SIZE[props.size]);

const getContainerClasses = computed(() => ({
  'is-active': props.isActive,
  'is-default': props.variant === GamChipVariant.DEFAULT,
  'is-primary': props.variant === GamChipVariant.PRIMARY,
  'is-error': props.variant === GamChipVariant.ERROR,
}));

const getLeftIcon = computed((): GamIconType | undefined => {
  if (props.leftIcon) {
    return getIconValues(props.leftIcon);
  }
  return undefined;
});

const getRightIcon = computed((): GamIconType | undefined => {
  if (props.rightIcon) {
    return getIconValues(props.rightIcon);
  }
  return undefined;
});

/* Selection reads as colour alone, which no screen reader can see, so the state
   has to be spelled out. */
const getAriaPressed = computed((): boolean | undefined => (isToggle.value ? props.isActive : undefined));

const handleKeydown = (event: KeyboardEvent) => {
  if (!isInteractive.value || (event.key !== 'Enter' && event.key !== ' ')) {
    return;
  }

  event.preventDefault();
  (event.currentTarget as HTMLElement).click();
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/partial';

.gam-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: max-content;
  flex-shrink: 0;
}

.is-clickable {
  cursor: pointer;
}

.content {
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
  white-space: nowrap;
  word-spacing: -0.15em;
}

.normal {
  padding: calc(var(--spacing-xs) + 2px) var(--spacing-md);
  gap: 5px;
  // height: 24px;

  &.is-active:not(.is-toggle) {
    padding: calc(var(--spacing-xs) - 0px) var(--spacing-md);
  }

  .content {
    line-height: 14px;
    min-height: 14px;
    font-size: 13px;
    letter-spacing: 0.02em;
  }
}

.small {
  padding: calc(var(--spacing-xxs) + 2px) calc(var(--spacing-xs) + var(--spacing-xxs));
  min-width: 20px;
  gap: 4px;

  &.is-active:not(.is-toggle) {
    padding: calc(var(--spacing-xxs) - 0px) calc(var(--spacing-xs) + var(--spacing-xxs));
  }

  .content {
    line-height: 16px;
    min-height: 16px;
    font-size: 12px;
    letter-spacing: 0.03em;
  }
}

.x-small {
  padding: calc(var(--spacing-xxs) + 2px) var(--spacing-s);
  min-width: 18px;
  height: 22px;
  gap: 3px;

  &.is-active:not(.is-toggle) {
    padding: calc(var(--spacing-xxs) - 0px) var(--spacing-s);
    height: 20px;
    // padding-left: calc(var(--spacing-s) - 1px);
    // padding-right: calc(var(--spacing-s) + 1px);
  }

  .content {
    line-height: 12px;
    min-height: 12px;
    font-size: 11px;
    letter-spacing: 0.05em;
    font-weight: 700;
    // margin-left: -1px;
  }
}

/* Rating dots are themselves a row of squares on a 3px rhythm, so the glyph
   needs a wider gap than a label does or it reads as one more dot. */
.gam-chip.has-dots {
  gap: 6px;

  &.x-small {
    gap: 5px;
  }
}

.icon {
  position: relative !important;
}

.upper-case {
  .content {
    text-transform: uppercase;
  }
}

.gam-chip {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-normal);
  position: relative;
}

.is-default {
  background: var(--color-white-8);
  color: var(--color-white-100);

  .content {
    -webkit-font-smoothing: antialiased;
  }

  &.is-outline {
    background: none !important;
    color: var(--color-white-100) !important;

    &:before {
      background: var(--color-white-10);
    }

    .content {
      -webkit-font-smoothing: antialiased;
    }

    &.is-active {
      &:before {
        background: var(--color-white-70);
      }
    }
  }

  &.is-active {
    background: var(--color-white-10);
    color: var(--color-white-100);
  }

  /* Nothing may change the box of a toggle: an icon or a weight swap on select
     would re-wrap the row it sits in, so selection is carried by colour alone.
     A choice has to read as picked across the room, so it inverts, while tags on
     a card keep the quiet ghost fill since they are labels, not choices. */
  &.is-toggle {
    transition:
      background 150ms linear,
      color 150ms linear;
  }

  &.is-toggle:not(.is-active) {
    color: var(--color-white-50);
  }

  &.is-toggle.is-active {
    background: var(--color-white);
    color: var(--color-dark-900);
  }
}

.is-primary {
  background: var(--color-linear-gradient-30);
  color: var(--color-pink);

  .content {
    background: var(--color-linear-gradient);
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &.is-active {
    background: var(--color-linear-gradient-10);
    color: var(--color-pink);

    .content {
      background: var(--color-linear-gradient);
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
}

.is-error {
  background: var(--color-red-30);
  color: var(--color-red);

  &.is-outline {
    background: none !important;
    color: var(--color-red) !important;

    &:before {
      background: var(--color-red-30);
    }

    &.is-active {
      border-color: var(--color-red);

      &:before {
        background: var(--color-red);
      }
    }
  }

  &.is-active {
    background: var(--color-red-10);
    color: var(--color-red);
  }
}
</style>
